(() => {
  const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
  let activeModalTrigger = null;

  const normalizeVariant = (variant) => {
    const normalizedVariant = String(variant || '')
      .trim()
      .toLowerCase()
      .replace(/^symbol-/, '');

    return normalizedVariant === 'a' || normalizedVariant === 'b' ? normalizedVariant : null;
  };

  const urlVariant = normalizeVariant(
    new URLSearchParams(window.location.search).get('version')
  );

  const getModalElements = () => {
    const popup = document.getElementById('info-popup');

    if (!popup) return null;

    const video = popup.querySelector('.info-popup-video');
    const closeButton = popup.querySelector('.popup-close');
    const overlay = popup.querySelector('.popup-overlay');

    if (!video || !closeButton || !overlay) return null;

    return { popup, video, closeButton, overlay };
  };

  const closeModal = () => {
    const modal = getModalElements();

    if (!modal) return;

    const wasActive = modal.popup.classList.contains('active');

    if (!wasActive && !modal.video.getAttribute('src')) return;

    modal.popup.classList.remove('active');
    modal.popup.setAttribute('aria-hidden', 'true');
    modal.video.pause();
    modal.video.removeAttribute('src');
    modal.video.load();

    activeModalTrigger?.focus({ preventScroll: true });
    activeModalTrigger = null;
  };

  const setupModal = () => {
    const modal = getModalElements();

    if (!modal || modal.popup.dataset.userStory3Initialized === 'true') return modal;

    modal.popup.dataset.userStory3Initialized = 'true';
    modal.popup.setAttribute('aria-hidden', 'true');
    modal.closeButton.addEventListener('click', closeModal);
    modal.overlay.addEventListener('click', closeModal);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.popup.classList.contains('active')) {
        closeModal();
      }
    });

    return modal;
  };

  const openModal = (source, trigger) => {
    const modal = setupModal();

    if (!modal || !source) return;

    activeModalTrigger = trigger;
    modal.video.src = source;
    modal.popup.classList.add('active');
    modal.popup.setAttribute('aria-hidden', 'false');
    modal.video.load();
    modal.video.play().catch(() => {
      // Controls remain available when autoplay is blocked by the browser.
    });
  };

  class UserStory3Card extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.cardRoot = this.querySelector('.user-story-3-card');
      this.playButton = this.querySelector('.user-story-3-play-button');
      this.inlineVideo = this.querySelector('.user-story-3-part-1-main.version-a > video');

      const initialVariant = urlVariant || 'a';
      this.setVariant(initialVariant, {
        emitEvent: false,
        source: urlVariant ? 'url' : 'default'
      });

      if (!this.playButton || !this.inlineVideo) return;

      this.handlePlayButtonClick = this.handlePlayButtonClick.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);

      this.playButton.addEventListener('click', this.handlePlayButtonClick);
      if (mobileMediaQuery.addEventListener) {
        mobileMediaQuery.addEventListener('change', this.handleViewportChange);
      } else {
        mobileMediaQuery.addListener(this.handleViewportChange);
      }
      setupModal();
    }

    disconnectedCallback() {
      this.playButton?.removeEventListener('click', this.handlePlayButtonClick);
      if (mobileMediaQuery.removeEventListener) {
        mobileMediaQuery.removeEventListener('change', this.handleViewportChange);
      } else {
        mobileMediaQuery.removeListener(this.handleViewportChange);
      }
      this.isInitialized = false;
    }

    get variant() {
      return this.getVariant();
    }

    set variant(variant) {
      this.setVariant(variant);
    }

    getVariant() {
      const cardRoot = this.cardRoot || this.querySelector('.user-story-3-card');

      if (!cardRoot) return null;
      if (cardRoot.classList.contains('symbol-b')) return 'b';
      if (cardRoot.classList.contains('symbol-a')) return 'a';

      return null;
    }

    revealVariant() {
      this.removeAttribute('data-ab-pending');
      this.setAttribute('aria-busy', 'false');
    }

    setVariant(variant, options = {}) {
      const normalizedVariant = urlVariant || normalizeVariant(variant);
      const cardRoot = this.cardRoot || this.querySelector('.user-story-3-card');

      if (!normalizedVariant || !cardRoot) return false;

      const previousVariant = this.getVariant();
      const hasChanged = previousVariant !== normalizedVariant;

      if (hasChanged) {
        this.resetInlineVideo();
        closeModal();
      }

      cardRoot.classList.remove('symbol-a', 'symbol-b');
      cardRoot.classList.add(`symbol-${normalizedVariant}`);
      this.dataset.variant = normalizedVariant;
      this.dataset.variantSource = urlVariant ? 'url' : (options.source || 'api');
      this.revealVariant();

      if (hasChanged && options.emitEvent !== false) {
        this.dispatchEvent(new CustomEvent('user-story-3:variant-change', {
          bubbles: true,
          detail: {
            variant: normalizedVariant,
            previousVariant,
            source: this.dataset.variantSource,
            card: this
          }
        }));
      }

      return true;
    }

    getVideoSource() {
      const source = this.inlineVideo?.querySelector('source');

      return source?.src || source?.getAttribute('src') || this.inlineVideo?.currentSrc || '';
    }

    handlePlayButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (mobileMediaQuery.matches) {
        this.playInlineVideo();
        return;
      }

      openModal(this.getVideoSource(), this.playButton);
    }

    playInlineVideo() {
      this.classList.add('is-video-playing');
      this.inlineVideo.controls = true;
      this.inlineVideo.setAttribute('controls', 'controls');
      this.inlineVideo.setAttribute('playsinline', 'playsinline');
      this.playButton.disabled = true;

      this.inlineVideo.play().catch(() => {
        // Keep the video and its controls visible so playback can be started manually.
      });
    }

    resetInlineVideo() {
      if (!this.classList.contains('is-video-playing')) return;

      this.inlineVideo.pause();
      this.inlineVideo.currentTime = 0;
      this.classList.remove('is-video-playing');
      this.playButton.disabled = false;
    }

    handleViewportChange(event) {
      if (event.matches) {
        closeModal();
        return;
      }

      this.resetInlineVideo();
    }
  }

  if (!customElements.get('user-story-3-card')) {
    customElements.define('user-story-3-card', UserStory3Card);
  }

  const resolveCards = (target) => {
    let targetElements;

    if (!target) {
      targetElements = Array.from(document.querySelectorAll('user-story-3-card'));
    } else if (typeof target === 'string') {
      targetElements = Array.from(document.querySelectorAll(target));
    } else if (target instanceof Element) {
      targetElements = [target];
    } else if (typeof target[Symbol.iterator] === 'function') {
      targetElements = Array.from(target);
    } else {
      targetElements = [];
    }

    const cards = targetElements
      .map((element) => {
        if (!(element instanceof Element)) return null;
        if (element.matches('user-story-3-card')) return element;

        return element.closest('user-story-3-card') || element.querySelector('user-story-3-card');
      })
      .filter(Boolean);

    return Array.from(new Set(cards));
  };

  window.UserStory3AB = {
    setVariant(variant, target) {
      const normalizedVariant = urlVariant || normalizeVariant(variant);

      if (!normalizedVariant) return 0;

      const cards = resolveCards(target);
      cards.forEach((card) => card.setVariant(normalizedVariant, { source: 'api' }));

      return cards.length;
    },

    getVariants(target) {
      return resolveCards(target).map((card) => ({
        card,
        variant: card.getVariant(),
        source: card.dataset.variantSource || null
      }));
    },

    getForcedVariant() {
      return urlVariant;
    }
  };

  window.dispatchEvent(new CustomEvent('user-story-3:ab-ready', {
    detail: {
      api: window.UserStory3AB,
      forcedVariant: urlVariant
    }
  }));
})();
